import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, AlertCircle, RotateCcw, ArrowRight, Trophy, Award } from "lucide-react";
import BrandName from "@/components/site/BrandName";
import PremiumCertificate from "@/components/site/PremiumCertificate";
import ScaledCertificate from "@/components/certificate/ScaledCertificate";
import { exportCertificatePDF } from "@/app/pdf";
import Button from "@/components/site/Button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Every question below is verified against UK statute — no invented facts.
// Sources cited inline so admins can audit the demo module.
const questions = [
  {
    q: "Under the Misuse of Drugs Regulations 2001 (reg 19), when must a Schedule 2 controlled drug transaction be entered into the CD register?",
    options: [
      "Within one hour of the transaction",
      "By the end of the day following the transaction",
      "Within 7 days",
      "At the next monthly stock audit",
    ],
    correct: 1,
    topic: "Record Keeping",
    source: "Misuse of Drugs Regulations 2001, reg 19(1)(b)",
  },
  {
    q: "Under the Controlled Drugs (Supervision of Management and Use) Regulations 2013, who may witness the destruction of stock Schedule 2 controlled drugs in a pharmacy?",
    options: [
      "Any two members of pharmacy staff",
      "The responsible pharmacist acting alone",
      "A person authorised in writing by the Accountable Officer",
      "A local police officer only",
    ],
    correct: 2,
    topic: "Regulatory Compliance",
    source: "CD (Supervision of Management and Use) Regs 2013",
  },
  {
    q: "For how long must a Schedule 2 controlled drug register be retained after the date of the last entry?",
    options: [
      "6 months",
      "2 years",
      "5 years",
      "10 years",
    ],
    correct: 1,
    topic: "Record Retention",
    source: "Misuse of Drugs Regulations 2001, reg 19(3)",
  },
];

function makeCertId(name) {
  const seed = (name || "guest").toString().replace(/\s+/g, "").toUpperCase().slice(0, 4).padEnd(4, "X");
  const rand = Math.floor(Math.random() * 90000 + 10000);
  return `CC.2026.${seed}${rand}`;
}

export default function TryIt() {
  const [stage, setStage] = useState("intro"); // intro, quiz, results, cert
  const [name, setName] = useState("");
  const [org, setOrg] = useState("");
  const [emailAddr, setEmailAddr] = useState("");
  const [answers, setAnswers] = useState([null, null, null]);
  const [idx, setIdx] = useState(0);
  const [certId] = useState(() => makeCertId(name));
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [downloading, setDownloading] = useState(false);

  const correctCount = answers.reduce(
    (n, a, i) => (a === questions[i].correct ? n + 1 : n),
    0
  );
  const scorePct = Math.round((correctCount / questions.length) * 100);
  const passed = scorePct >= 66;

  const certProps = {
    staffName: name,
    organisation: org || "Independent practitioner",
    competencyTitle: "Controlled Drug Handling",
    sopReferences: ["SOP-CD-06 v6.2"],
    completedOn: new Date().toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    validUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    score: `${scorePct}%`,
    passMark: "66%",
    certId,
  };

  async function downloadPdf() {
    setDownloading(true);
    try {
      const safeName = (name || "certificate")
        .replace(/[^a-z0-9]+/gi, "-")
        .toLowerCase();
      await exportCertificatePDF(certProps, `${safeName}-${certId}.pdf`);
    } finally {
      setDownloading(false);
    }
  }

  function reset() {
    setStage("intro");
    setAnswers([null, null, null]);
    setIdx(0);
    setSubmitStatus("idle");
  }

  // As soon as the user finishes the quiz (results stage), submit the
  // score to the backend. Firing here — not on cert stage — means the
  // assessment is captured even if the user closes the tab before
  // reaching the certificate.
  useEffect(() => {
    if (stage !== "results" || submitStatus !== "idle") return;
    setSubmitStatus("sending");
    fetch(`${API}/assessment`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name,
        organisation: org,
        email: emailAddr,
        score: correctCount,
        total: questions.length,
        passed,
        certificate_id: certId,
        answers,
      }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("submit-failed"))))
      .then(() => setSubmitStatus("sent"))
      .catch(() => setSubmitStatus("error"));
  }, [stage, submitStatus, name, org, emailAddr, correctCount, passed, certId, answers]);

  return (
    <section
      id="tryit"
      data-testid="tryit-section"
      className="relative py-14 md:py-24 border-t border-white/[0.04]"
    >
      <div className="absolute inset-0 pointer-events-none grid-lines opacity-30" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-12">
          <div className="max-w-xl">
            <div className="eyebrow" style={{ color: "#D4A65A" }}>
              Interactive demonstration
            </div>
            <h2 className="mt-3 font-display text-4xl md:text-[44px] tracking-[-0.035em] text-white leading-[1.02] font-semibold">
              Try a real assessment. Take home a real certificate.
            </h2>
          </div>
          <p className="max-w-sm text-slate-200 leading-relaxed text-[15px]">
            Answer three genuine controlled drug questions. If you score well
            enough, we mint your personal verifiable certificate on the spot.
          </p>
        </div>

        <div className="relative rounded-xl border border-white/[0.08] bg-gradient-to-br from-[#0B1424] to-[#050B18] overflow-hidden">
          <AnimatePresence mode="wait">
            {stage === "intro" && (
              <motion.div
                key="intro"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
                className="grid md:grid-cols-5 gap-0"
              >
                <div className="md:col-span-3 p-8 md:p-12">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#D4A65A]">
                    <span className="h-1 w-8 bg-[#D4A65A]/60" />
                    Sample module
                  </div>
                  <h3 className="mt-4 font-display text-3xl md:text-4xl text-white tracking-[-0.03em] leading-[1.05] font-semibold">
                    Controlled Drug Handling SOP v6.2
                  </h3>
                  <p className="mt-4 text-slate-200 text-[14.5px] leading-relaxed max-w-md">
                    Three multiple choice questions drawn from a live UKCSL
                    training bank. Pass mark is 66%. Your certificate is
                    generated instantly and time stamped.
                  </p>

                  <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
                    <label className="block">
                      <span
                        className="text-[12px] uppercase tracking-[0.18em] font-bold"
                        style={{ color: "#FFFFFF" }}
                      >
                        Full name
                      </span>
                      <input
                        data-testid="tryit-name-input"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Amelia Patel"
                        className="mt-1.5 w-full rounded-md border border-white/40 bg-white/[0.12] px-3 py-2.5 text-[14px] text-white placeholder:text-slate-300 focus:border-[#FFD98A] focus:outline-none focus:ring-2 focus:ring-[#FFD98A]/40 transition-colors"
                      />
                    </label>
                    <label className="block">
                      <span
                        className="text-[12px] uppercase tracking-[0.18em] font-bold"
                        style={{ color: "#FFFFFF" }}
                      >
                        Organisation
                      </span>
                      <input
                        data-testid="tryit-org-input"
                        type="text"
                        value={org}
                        onChange={(e) => setOrg(e.target.value)}
                        placeholder="Meridian Pharma"
                        className="mt-1.5 w-full rounded-md border border-white/40 bg-white/[0.12] px-3 py-2.5 text-[14px] text-white placeholder:text-slate-300 focus:border-[#FFD98A] focus:outline-none focus:ring-2 focus:ring-[#FFD98A]/40 transition-colors"
                      />
                    </label>
                  </div>

                  <Button
                    data-testid="tryit-start-btn"
                    onClick={() => {
                      if (!name.trim()) {
                        document.querySelector("[data-testid='tryit-name-input']")?.focus();
                        return;
                      }
                      setStage("quiz");
                    }}
                    variant="primary"
                    size="lg"
                    className="mt-8"
                  >
                    Begin assessment
                    <ArrowRight size={16} strokeWidth={2.75} />
                  </Button>

                  <div className="mt-6 text-[12px] text-slate-300">
                    Takes 90 seconds. Your details stay on this device.
                  </div>
                </div>

                {/* Right visual */}
                <div className="md:col-span-2 relative border-t md:border-t-0 md:border-l border-white/[0.06] bg-[#08111F] p-8 flex flex-col justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      What you receive
                    </div>
                    <ul className="mt-5 space-y-4">
                      {[
                        ["Personalised certificate", "Your name, org, score"],
                        ["Verification reference", "Certificate, Assessment & Org IDs"],
                        ["Time stamped record", "Kept for 7 years"],
                        ["Downloadable PDF", "Share with your team"],
                      ].map(([a, b]) => (
                        <li key={a} className="flex items-start gap-3">
                          <span className="mt-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-[#D4A65A]/40 bg-[#D4A65A]/10 shrink-0">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#D4A65A]" />
                          </span>
                          <div>
                            <div className="text-white text-sm">{a}</div>
                            <div className="text-slate-500 text-[11px]">
                              {b}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-6 text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    Powered by <BrandName />
                  </div>
                </div>
              </motion.div>
            )}

            {stage === "quiz" && (
              <motion.div
                key="quiz"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
                className="p-8 md:p-12"
              >
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#E6BE7A] font-semibold font-mono">
                      Q.0{idx + 1}
                    </span>
                    <span className="h-px w-8 bg-white/20" />
                    <span className="text-[11px] uppercase tracking-[0.2em] text-slate-200 font-semibold">
                      {questions[idx].topic}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[12px] text-slate-200 font-medium">
                    <div className="h-1.5 w-32 rounded-full bg-white/[0.10] overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#D4A65A]/60 to-[#D4A65A]"
                        style={{
                          width: `${((idx + 1) / questions.length) * 100}%`,
                        }}
                      />
                    </div>
                    Question {idx + 1} of {questions.length}
                  </div>
                </div>

                <h3 className="mt-6 font-display text-2xl md:text-3xl text-white tracking-[-0.02em] leading-[1.2] max-w-3xl">
                  {questions[idx].q}
                </h3>

                <div className="mt-8 grid gap-3 max-w-3xl">
                  {questions[idx].options.map((opt, i) => {
                    const selected = answers[idx] === i;
                    return (
                      <button
                        key={opt}
                        data-testid={`tryit-q${idx}-opt${i}`}
                        onClick={() => {
                          const next = [...answers];
                          next[idx] = i;
                          setAnswers(next);
                        }}
                        className={`text-left flex items-center justify-between gap-4 rounded-lg border px-5 py-4 transition-all ${
                          selected
                            ? "border-[#E6BE7A] bg-[#D4A65A]/20 shadow-[0_0_0_1px_rgba(212,166,90,0.45)]"
                            : "border-white/25 bg-white/[0.06] hover:border-white/50 hover:bg-white/[0.10]"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={`h-4 w-4 rounded-full border flex items-center justify-center ${
                              selected
                                ? "border-[#E6BE7A]"
                                : "border-white/50"
                            }`}
                          >
                            {selected && (
                              <span className="h-2 w-2 rounded-full bg-[#E6BE7A]" />
                            )}
                          </span>
                          <span
                            className={`text-[15px] ${
                              selected ? "text-white font-semibold" : "text-slate-100"
                            }`}
                          >
                            {opt}
                          </span>
                        </div>
                        <span className="text-[11px] uppercase tracking-[0.16em] text-slate-300 font-mono font-semibold">
                          {String.fromCharCode(65 + i)}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-10 flex items-center justify-between">
                  <button
                    onClick={() => (idx > 0 ? setIdx(idx - 1) : setStage("intro"))}
                    className="text-[13px] text-slate-200 hover:text-white font-semibold transition-colors"
                  >
                    Back
                  </button>
                  <Button
                    data-testid="tryit-next-btn"
                    disabled={answers[idx] === null}
                    onClick={() => {
                      if (idx < questions.length - 1) setIdx(idx + 1);
                      else setStage("results");
                    }}
                    variant="primary"
                    size="md"
                  >
                    {idx < questions.length - 1 ? "Next question" : "See my result"}
                    <ArrowRight size={14} />
                  </Button>
                </div>
              </motion.div>
            )}

            {stage === "results" && (
              <motion.div
                key="results"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
                className="p-10 md:p-16"
                data-testid="tryit-results"
              >
                <div className="max-w-2xl mx-auto text-center">
                  {/* Trophy / alert icon */}
                  <div
                    className={`mx-auto inline-flex h-20 w-20 items-center justify-center rounded-full ${
                      passed
                        ? "bg-[#FFD54F]/15 ring-4 ring-[#FFD54F]/40"
                        : "bg-rose-500/10 ring-4 ring-rose-400/40"
                    }`}
                    data-testid="tryit-results-badge"
                  >
                    {passed ? (
                      <Trophy size={40} style={{ color: "#FFD54F" }} strokeWidth={2.25} />
                    ) : (
                      <AlertCircle size={40} className="text-rose-300" strokeWidth={2.25} />
                    )}
                  </div>

                  {/* Heading */}
                  <h3
                    className="mt-6 font-display text-4xl md:text-5xl tracking-[-0.03em] leading-[1.05] font-semibold"
                    style={{ color: "#FFFFFF" }}
                    data-testid="tryit-results-heading"
                  >
                    {passed
                      ? `Congratulations${name ? ", " + name.split(" ")[0] : ""}!`
                      : "So close — nearly there"}
                  </h3>

                  <p
                    className="mt-4 text-[16px] leading-relaxed max-w-lg mx-auto"
                    style={{ color: "#E2E8F0" }}
                  >
                    {passed
                      ? "You demonstrated the required level of understanding on the Controlled Drug Handling assessment. Your verifiable certificate is ready."
                      : "You didn't quite hit the 66% pass mark this time. In a real workspace your team lead would review the questions with you and set a retake."}
                  </p>

                  {/* Big score display */}
                  <div
                    className="mt-10 inline-flex flex-col items-center gap-2 rounded-2xl border px-10 py-8"
                    style={{
                      borderColor: passed ? "rgba(255, 213, 79, 0.45)" : "rgba(244, 63, 94, 0.35)",
                      background: passed
                        ? "linear-gradient(180deg, rgba(255, 213, 79, 0.10) 0%, rgba(255, 213, 79, 0.03) 100%)"
                        : "linear-gradient(180deg, rgba(244, 63, 94, 0.10) 0%, rgba(244, 63, 94, 0.03) 100%)",
                    }}
                    data-testid="tryit-results-score"
                  >
                    <span
                      className="text-[11px] uppercase tracking-[0.25em] font-bold"
                      style={{ color: "#FFFFFF" }}
                    >
                      Your score
                    </span>
                    <div className="flex items-baseline gap-2">
                      <span
                        className="font-display font-bold tracking-tight"
                        style={{
                          fontSize: "72px",
                          lineHeight: 1,
                          color: passed ? "#FFD54F" : "#FDA4AF",
                        }}
                      >
                        {correctCount}
                      </span>
                      <span className="text-3xl font-semibold" style={{ color: "#FFFFFF" }}>
                        / {questions.length}
                      </span>
                    </div>
                    <div className="text-[13px]" style={{ color: "#CBD5E1" }}>
                      {scorePct}% &middot; pass mark 66%
                    </div>
                  </div>

                  {/* Primary CTA */}
                  <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                    {passed ? (
                      <Button
                        data-testid="tryit-get-cert-btn"
                        onClick={() => setStage("cert")}
                        variant="primary"
                        size="lg"
                      >
                        <Award size={18} strokeWidth={2.5} />
                        Get my certificate
                        <ArrowRight size={16} strokeWidth={2.75} />
                      </Button>
                    ) : (
                      <Button
                        data-testid="tryit-retry-btn"
                        onClick={reset}
                        variant="primary"
                        size="lg"
                      >
                        <RotateCcw size={16} strokeWidth={2.5} />
                        Try again
                      </Button>
                    )}

                    <button
                      onClick={reset}
                      data-testid="tryit-results-reset"
                      className="text-[13px] font-semibold underline underline-offset-4 transition-colors"
                      style={{ color: "#CBD5E1" }}
                    >
                      Start over
                    </button>
                  </div>

                  {/* Submission status */}
                  <div className="mt-8 text-[11px] uppercase tracking-[0.2em]" style={{ color: "#94A3B8" }}>
                    {submitStatus === "sent" ? (
                      <span style={{ color: "#FFD54F" }}>Result logged &middot; ref {certId}</span>
                    ) : submitStatus === "sending" ? (
                      "Recording your result…"
                    ) : submitStatus === "error" ? (
                      <span className="text-rose-300">
                        We couldn't log this result — please book a demo.
                      </span>
                    ) : (
                      "Preparing your record…"
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {stage === "cert" && (
              <motion.div
                key="cert"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
                className="p-8 md:p-12"
              >
                {/* Result banner */}
                <div
                  className={`flex items-center gap-4 rounded-lg px-5 py-4 border ${
                    passed
                      ? "border-[#D4A65A]/40 bg-[#D4A65A]/[0.06]"
                      : "border-rose-400/40 bg-rose-500/[0.06]"
                  }`}
                >
                  {passed ? (
                    <CheckCircle2 className="text-[#D4A65A]" size={22} />
                  ) : (
                    <AlertCircle className="text-rose-300" size={22} />
                  )}
                  <div className="flex-1">
                    <div className="text-white font-semibold tracking-tight">
                      {passed ? "Passed. Certificate issued." : "Not quite. Try again."}
                    </div>
                    <div className="text-[12px] text-slate-400 mt-0.5">
                      You scored {correctCount} of {questions.length} ({scorePct}%).
                      Pass mark is 66%.
                    </div>
                  </div>
                  <button
                    onClick={reset}
                    data-testid="tryit-reset-btn"
                    className="inline-flex items-center gap-2 text-[12px] text-slate-300 hover:text-white border border-white/10 hover:border-white/25 rounded-md px-3 py-1.5 transition-colors"
                  >
                    <RotateCcw size={12} />
                    Reset
                  </button>
                </div>

                {passed && (
                  <div className="mt-8 space-y-4" data-testid="tryit-certificate">
                    <ScaledCertificate {...certProps} />

                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-5 py-3">
                      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                        {submitStatus === "sent" ? (
                          <span className="text-[#D4A65A]">
                            Result routed to wayne@ukcsl.co.uk
                          </span>
                        ) : submitStatus === "sending" ? (
                          "Recording result…"
                        ) : submitStatus === "error" ? (
                          <span className="text-rose-300">
                            Could not record result. Please book a demo.
                          </span>
                        ) : (
                          "Preparing your record…"
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={downloadPdf}
                          disabled={downloading}
                          data-testid="tryit-download-btn"
                          variant="ghost"
                          size="sm"
                        >
                          {downloading ? "Building PDF…" : "Download PDF"}
                        </Button>
                        <Button
                          as="a"
                          href="#contact"
                          data-testid="tryit-demo-btn"
                          variant="primary"
                          size="sm"
                        >
                          Book a full demo
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {!passed && (
                  <div className="mt-8 text-slate-400 text-[14px]">
                    Real staff would receive coaching and a second attempt.
                    Reset and try again to see the certificate flow.
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
